import * as endpoints from "../../lib/api/endpoints";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton from "../ActionButton";
import Checkbox from "../Checkbox";
import Column from "../grid/Column";
import Container from "../grid/Container";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import Header from "../Header";
import LinedInput, {InputType} from "../LinedInput";
import PhoneInput from "../PhoneInput";
import React, {createRef, useEffect, useState, useRef, useMemo, useCallback} from "react";
import Row from "../grid/Row";
import SetNoIndex from "../../lib/SetNoIndex";
import classNames from "../../lib/classnames";
import styles from "./CongratulationsPage.module.css";
import useFocusInvalid from "../../lib/useFocusInvalid";
import usePageView from "../../lib/usePageView";
import ValidationList from "../ValidationList";
import {AppSecurityValidation, AppSecurityKeys} from "../../lib/api/models";
import {RouteComponentProps, Redirect} from "react-router-dom";
import {StaticContext} from "react-router";
import InstantCardModal from "./InstantCardModal";
import CongratulationsCheck from "../icons/CongratulationsCheck";

const strings = {
    title: "1st Financial Bank USA Congrats",
    header: "Congratulations!",
    p1: "You've been approved for a 1st Financial Bank USA credit card.",
    p2:
        "For security purposes and to help us manage your account, please provide the information below:",
    mothersMaidenName: "Mother's Maiden Name",
    p3:
        "Enter the person who should be contacted if the Bank needs to reach you while you are travelling or otherwise cannot be reached.",
    contactName: "Contact Name",
    contactRelationship: "Relationship (parent, other family member, or friend)",
    contactAddress: "Contact Address",
    contactStreet: "Street",
    contactStreet2: "Apt / unit",
    contactCity: "City",
    contactState: "State",
    contactZip: "ZIP Code",
    contactHomePhone: "Contact Home Phone",
    permission:
        "I give the Bank permission to provide information about my account to the person named above.",
    p4: "You should receive your credit card at the address on your application within 10-14 days.",

    instantCardP5:
        'If you would like to start using your card now for online purchases, click "Yes, show me my account information now!" (you will see your account number and the information needed to use your account to make online purchases), otherwise click "Finish (I\'ll wait for my card to arrive in the mail)."',
    yesShowMe: "Yes, show me my account information now!",
    finishWait: "Finish (I'll wait for my card to arrive in the mail)",

    finish: "Finish",
};

const EMPTY_SECURITY_VALIDATION: AppSecurityValidation = {};
const ORDERED_FIELDS: string[] = [
    AppSecurityKeys.MothersMaidenName,
    AppSecurityKeys.ContactName,
    AppSecurityKeys.ContactRelationship,
    AppSecurityKeys.ContactAddress,
    AppSecurityKeys.ContactHomePhone,
    AppSecurityKeys.ContactPermission,
];

type RouterState = {
    id?: string;
};

export default function CongratulationsPage(
    props: RouteComponentProps<undefined, StaticContext, RouterState>,
) {
    useEffect(() => {
        document.title = strings.title;
    }, []);

    SetNoIndex();
    usePageView(strings.title);

    const queryParams = useMemo(() => queryStringHelpers.objectify(window.location.search), []);

    const isInstantCard = queryParams.instantCard === "true";

    const [mothersMaidenName, setMothersMaidenName] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactRelationship, setContactRelationship] = useState("");
    const [contactAddress, setContactAddress] = useState("");
    const [contactHomePhone, setContactHomePhone] = useState("");
    const [contactPermission, setcontactPermission] = useState(false);

    const [validation, setValidation] = useState<validationLib.Validation>();
    const [validationCount, setValidationCount] = useState(0);

    const securityValidation = validation?.errors ?? EMPTY_SECURITY_VALIDATION;

    const [loading, setLoading] = useState(false);

    const [showInstantCardModal, setShowInstantCardModal] = useState(false);

    const inputRefs = useRef<{[key: string]: React.RefObject<HTMLInputElement>}>({
        [AppSecurityKeys.MothersMaidenName]: createRef(),
        [AppSecurityKeys.ContactName]: createRef(),
        [AppSecurityKeys.ContactRelationship]: createRef(),
        [AppSecurityKeys.ContactAddress]: createRef(),
        [AppSecurityKeys.ContactHomePhone]: createRef(),
        [AppSecurityKeys.ContactPermission]: createRef(),
    });

    const showInstantCardBtnRef = useRef(null);

    let firstInvalidKey: string | undefined = undefined;
    firstInvalidKey = ORDERED_FIELDS.find((field) => {
        return securityValidation[field as AppSecurityKeys];
    });

    const firstInvalidRef = firstInvalidKey ? inputRefs.current[firstInvalidKey] : undefined;

    useFocusInvalid(validationCount, firstInvalidRef?.current);

    const onFinishNavigation = useCallback(() => {
        const applicationId = props.location.state?.id;
        if (applicationId === undefined) {
            props.history.replace(routes.ORGANIC_LANDING);
            return;
        }

        const paramsToStringify: {[key: string]: string} = {
            ...queryParams,
            dsn: applicationId,
        };

        // Remove instantCard from query params
        delete paramsToStringify.instantCard;

        const stringParams = queryStringHelpers.stringify(paramsToStringify);
        // NOTE: Using full round-trip page transition here for security reasons
        const destination = `${process.env.REACT_APP_BASE_PATH}${routes.REFERRAL_PAGE}${stringParams}`;
        window.location.replace(destination);
    }, [props.location.state?.id, props.history, queryParams]);

    if (!props.location.state?.id) {
        return <Redirect to={routes.ORGANIC_LANDING} />;
    }

    function onFinish(showInstantCard: boolean) {
        const applicationId = props.location.state?.id;
        if (applicationId === undefined) {
            props.history.replace(routes.ORGANIC_LANDING);
            return;
        }

        setLoading(true);
        endpoints
            .applicationSecurity(
                applicationId,
                mothersMaidenName,
                contactName,
                contactRelationship,
                contactAddress,
                contactHomePhone,
                contactPermission,
            )
            .then(() => {
                if (showInstantCard) {
                    setShowInstantCardModal(true);
                } else {
                    onFinishNavigation();
                }
            })
            .catch((err) => {
                setValidation(validationLib.getValidationObj(err));
                setValidationCount((prev) => prev + 1);
            })
            .finally(() => setLoading(false));
    }

    function onFinishForm(e: React.FormEvent) {
        e.preventDefault();

        onFinish(false);
    }

    return (
        <ErrorBoundary>
            <Header />
            <form onSubmit={onFinishForm}>
                <Container className={styles.container}>
                    <Row className={styles.titleRow}>
                        <Column>
                            <div className={styles.title}>
                                <CongratulationsCheck className={styles.checkIcon} />
                                {strings.header}
                            </div>
                        </Column>
                    </Row>
                    <Row className={styles.p1Row}>
                        <Column>
                            <div className={classNames(styles.paragraph, styles.bold)}>
                                {strings.p1}
                            </div>
                        </Column>
                    </Row>
                    <Row className={styles.p2Row}>
                        <Column>
                            <div className={styles.paragraph}>{strings.p2}</div>
                        </Column>
                    </Row>
                    <Row className={styles.inputRow}>
                        <Column large={6} medium={4}>
                            <LinedInput
                                id="mothers-name"
                                value={mothersMaidenName}
                                setValue={(value) => {
                                    setMothersMaidenName(value);
                                    setValidation((prevValidation) => {
                                        const nextValidation = {
                                            ...prevValidation,
                                            errors: {
                                                ...prevValidation?.errors,
                                            },
                                        };
                                        delete nextValidation.errors[
                                            AppSecurityKeys.MothersMaidenName
                                        ];
                                        return nextValidation;
                                    });
                                }}
                                label={strings.mothersMaidenName}
                                type={InputType.Text}
                                maxLength={20}
                                validationMessage={
                                    securityValidation[AppSecurityKeys.MothersMaidenName]
                                }
                                ref={inputRefs.current[AppSecurityKeys.MothersMaidenName]}
                            />
                        </Column>
                    </Row>
                    <Row className={styles.p3Row}>
                        <Column>
                            <div className={styles.paragraph}>{strings.p3}</div>
                        </Column>
                    </Row>
                    <Row className={styles.inputRow}>
                        <Column large={6} medium={4}>
                            <LinedInput
                                id={AppSecurityKeys.ContactName}
                                value={contactName}
                                setValue={(value) => {
                                    setContactName(value);
                                    setValidation((prevValidation) => {
                                        const nextValidation = {
                                            ...prevValidation,
                                            errors: {
                                                ...prevValidation?.errors,
                                            },
                                        };
                                        delete securityValidation[AppSecurityKeys.ContactName];
                                        return nextValidation;
                                    });
                                }}
                                label={strings.contactName}
                                type={InputType.Text}
                                maxLength={30}
                                validationMessage={securityValidation[AppSecurityKeys.ContactName]}
                                ref={inputRefs.current[AppSecurityKeys.ContactName]}
                            />
                        </Column>
                    </Row>
                    <Row className={styles.inputRow}>
                        <Column large={6} medium={4}>
                            <LinedInput
                                id={AppSecurityKeys.ContactRelationship}
                                value={contactRelationship}
                                setValue={(value) => {
                                    setContactRelationship(value);
                                    setValidation((prevValidation) => {
                                        const nextValidation = {
                                            ...prevValidation,
                                            errors: {
                                                ...prevValidation?.errors,
                                            },
                                        };
                                        delete securityValidation[
                                            AppSecurityKeys.ContactRelationship
                                        ];
                                        return nextValidation;
                                    });
                                }}
                                label={strings.contactRelationship}
                                type={InputType.Text}
                                maxLength={15}
                                validationMessage={
                                    securityValidation[AppSecurityKeys.ContactRelationship]
                                }
                                ref={inputRefs.current[AppSecurityKeys.ContactRelationship]}
                            />
                        </Column>
                    </Row>
                    <Row className={styles.inputRow}>
                        <Column large={6} medium={4}>
                            <LinedInput
                                id={AppSecurityKeys.ContactAddress}
                                value={contactAddress}
                                setValue={(value) => {
                                    setContactAddress(value);
                                    setValidation((prevValidation) => {
                                        const nextValidation = {
                                            ...prevValidation,
                                            errors: {
                                                ...prevValidation?.errors,
                                            },
                                        };
                                        delete securityValidation[AppSecurityKeys.ContactAddress];
                                        return nextValidation;
                                    });
                                }}
                                label={strings.contactAddress}
                                type={InputType.Text}
                                maxLength={30}
                                validationMessage={
                                    securityValidation[AppSecurityKeys.ContactAddress]
                                }
                                ref={inputRefs.current[AppSecurityKeys.ContactAddress]}
                            />
                        </Column>
                    </Row>
                    <Row className={styles.inputRow}>
                        <Column large={6} medium={4}>
                            <PhoneInput
                                id={AppSecurityKeys.ContactHomePhone}
                                value={contactHomePhone}
                                setValue={(value) => {
                                    setContactHomePhone(value);
                                    setValidation((prevValidation) => {
                                        const nextValidation = {
                                            ...prevValidation,
                                            errors: {
                                                ...prevValidation?.errors,
                                            },
                                        };
                                        delete securityValidation[AppSecurityKeys.ContactHomePhone];
                                        return nextValidation;
                                    });
                                }}
                                inputMode="numeric"
                                label={strings.contactHomePhone}
                                validationMessage={
                                    securityValidation[AppSecurityKeys.ContactHomePhone]
                                }
                                ref={inputRefs.current[AppSecurityKeys.ContactHomePhone]}
                            />
                        </Column>
                    </Row>
                    <Row className={styles.checkboxRow}>
                        <Column large={8} medium={6}>
                            <Checkbox
                                label={strings.permission}
                                isChecked={contactPermission}
                                onChange={(e) => {
                                    setcontactPermission(e.target.checked);
                                    setValidation((prevValidation) => {
                                        const nextValidation = {
                                            ...prevValidation,
                                            errors: {
                                                ...prevValidation?.errors,
                                            },
                                        };
                                        delete securityValidation[
                                            AppSecurityKeys.ContactPermission
                                        ];
                                        return nextValidation;
                                    });
                                }}
                                hasGradient={true}
                                validationMessage={
                                    securityValidation[AppSecurityKeys.ContactPermission]
                                }
                                ref={inputRefs.current[AppSecurityKeys.ContactPermission]}
                            />
                        </Column>
                    </Row>
                    <Row className={styles.p4Row}>
                        <Column>
                            <div className={styles.paragraph}>{strings.p4}</div>
                        </Column>
                    </Row>
                    {isInstantCard && (
                        <Row className={styles.p5Row}>
                            <Column>
                                <div className={styles.paragraph}>{strings.instantCardP5}</div>
                            </Column>
                        </Row>
                    )}
                    <Row className={styles.finishRow}>
                        <Column>
                            {validation && validation.generic ? (
                                <ValidationList messages={validation.generic} />
                            ) : null}
                        </Column>
                        {isInstantCard ? (
                            <Column
                                large={6}
                                medium={6}
                                className={styles.instantCardButtonContainer}
                            >
                                <ActionButton
                                    ref={showInstantCardBtnRef}
                                    type="button"
                                    label={strings.yesShowMe}
                                    fullWidth={true}
                                    loading={loading}
                                    onClick={() => onFinish(true)}
                                />
                                <ActionButton
                                    label={strings.finishWait}
                                    fullWidth={true}
                                    loading={loading}
                                />
                            </Column>
                        ) : (
                            <Column large={4} medium={3}>
                                <ActionButton
                                    label={strings.finish}
                                    fullWidth={true}
                                    loading={loading}
                                />
                            </Column>
                        )}
                    </Row>
                </Container>
            </form>
            <Footer />
            <InstantCardModal
                isOpen={showInstantCardModal}
                onClose={onFinishNavigation}
                triggerElement={showInstantCardBtnRef?.current}
            />
        </ErrorBoundary>
    );
}
